import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

export const IMPERSONATION_FLAG = 'k3x.impersonation.active';
export const IMPERSONATION_EXPIRES_AT = 'k3x.impersonation.expiresAt';
export const IMPERSONATION_APP_NAME = 'impersonation';

export const getAppAuth = (): Auth =>
  sessionStorage.getItem(IMPERSONATION_FLAG) === '1'
    ? getAuth(getApp(IMPERSONATION_APP_NAME))
    : getAuth();

const originalAxiosInstance = Axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
});

/** Use for non-orval calls (e.g. `responseType: 'blob'`). */
export { originalAxiosInstance };

originalAxiosInstance.interceptors.request.use(
  async (config) => {
    const auth = getAppAuth();

    if (auth.currentUser && config.headers) {
      config.headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

export type BillingBlockedEventDetail = {
  code?: string;
  message?: string;
  balance?: number | null;
  required?: number | null;
};

originalAxiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<BillingBlockedEventDetail & { message?: string }>) => {
    const skipBillingGate = import.meta.env.VITE_SKIP_BILLING_GATE === 'true';

    if (
      error.response?.status === 403 &&
      error.response?.data?.message === 'Account has been disabled'
    ) {
      window.dispatchEvent(new CustomEvent('auth:access_disabled'));
    }

    if (error.response?.status === 402 && !skipBillingGate) {
      const body = error.response.data ?? {};
      window.dispatchEvent(
        new CustomEvent<BillingBlockedEventDetail>('billing:upgradeRequired', {
          detail: {
            code: body.code,
            message: body.message,
            balance: body.balance ?? null,
            required: body.required ?? null,
          },
        }),
      );
    }

    return Promise.reject(error);
  },
);

export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;

export const axiosInstance = <T>(url: string, options?: RequestInit): Promise<T> => {
  const { body, headers, ...config } = options || {};

  return originalAxiosInstance({
    url,
    data: body,
    headers: headers as any,
    ...config,
  } as AxiosRequestConfig);
};
